import { Fragment } from "react";
import {
  Check,
  CheckCircle,
  Circle,
  CreditCard,
  User,
  type LucideIcon,
} from "lucide-react";
import type { CheckoutProgressStep } from "@/lib/checkoutSteps";

/**
 * Icono representativo por paso; el mapa se ancla a las etiquetas del contrato
 * de `deriveSteps` y cae a un círculo neutro si aparece una etiqueta nueva.
 */
const STEP_ICONS: Record<string, LucideIcon> = {
  Datos: User,
  Pago: CreditCard,
  Confirmación: CheckCircle,
};

const STATE_LABELS = {
  done: "completado",
  active: "en curso",
  pending: "pendiente",
} as const;

/**
 * El estado se distingue por forma además de color: completado lleva check y
 * relleno, el activo un halo, y el pendiente un borde discontinuo sin relleno.
 */
const DOT_STYLES = {
  done: "bg-green-600 text-white",
  active: "bg-primary text-white ring-2 ring-primary/30 ring-offset-2",
  pending: "border-2 border-dashed border-gray-300 text-gray-400",
} as const;

interface CheckoutStepperProps {
  /** Pasos derivados por `deriveSteps`; el componente no los transforma. */
  steps: CheckoutProgressStep[];
}

export const CheckoutStepper = ({ steps }: CheckoutStepperProps) => (
  <div
    className="flex items-start justify-center"
    data-testid="checkout-stepper"
  >
    {steps.map((step, index) => {
      const state = step.done ? "done" : step.active ? "active" : "pending";
      const Icon = step.done ? Check : STEP_ICONS[step.label] ?? Circle;

      return (
        <Fragment key={step.label}>
          {index > 0 && (
            <div
              aria-hidden="true"
              data-testid="stepper-connector"
              data-filled={steps[index - 1].done}
              className={`mt-[15px] h-0.5 min-w-[16px] flex-1 rounded-full transition-colors duration-300 motion-reduce:transition-none ${
                steps[index - 1].done ? "bg-green-600" : "bg-gray-200"
              }`}
            />
          )}
          <div
            data-testid="stepper-step"
            data-state={state}
            aria-current={step.active ? "step" : undefined}
            className="flex w-[72px] shrink-0 flex-col items-center gap-1 sm:w-24"
          >
            <span className="sr-only">{`${step.label}: ${STATE_LABELS[state]}`}</span>
            <span
              className={`flex h-8 w-8 items-center justify-center rounded-full transition-colors duration-300 motion-reduce:transition-none ${DOT_STYLES[state]}`}
            >
              <Icon className="h-4 w-4" aria-hidden="true" />
            </span>
            <span
              aria-hidden="true"
              className={`max-w-full truncate text-[11px] font-medium sm:text-xs ${
                step.active
                  ? "text-primary"
                  : step.done
                    ? "text-green-600"
                    : "text-gray-400"
              }`}
            >
              {step.label}
            </span>
          </div>
        </Fragment>
      );
    })}
  </div>
);
